/**
 * Thread @-mention autocomplete — pure core (group-threading P2b follow-up).
 *
 * Powers the composer dropdown: given the account roster and the in-progress
 * `@query` (from `detectActiveMention`), rank the matching members and, for each,
 * derive the plain `@handle` the composer will insert. The handle is chosen so the
 * on-send resolver (`resolveMentionsToUids`) maps it back to EXACTLY the picked uid
 * — verified against the live roster — so the summon targets the right person while
 * the message text stays human-readable (no raw `<@uid>` tokens, no non-editable
 * pills in the fragile contentEditable). Pure + deterministic + React-free.
 */
import { isTeamMentionQuery, resolveMentionsToUids, TEAM_MENTION_HANDLE } from './mentionResolver';
import type { MentionRosterEntry } from './mentionResolver';

export interface MentionCandidate {
  uid: string;
  /** Human label for the row: displayName → email → uid. */
  label: string;
  /** Email shown as a muted second line only when a distinct displayName exists. */
  secondary?: string;
  /** The plain, resolver-safe handle inserted after `@` (e.g. `qa-teammate`). */
  handle: string;
  /**
   * Already in the active thread? (2026-09-15) ABSENT when the caller passed no
   * participant list — an unknown thread — and the pick behaves as it always
   * did. `false` marks the row "not in this thread" and makes the pick ASK
   * before anyone is added (see mentionAddConsent.ts).
   */
  inThread?: boolean;
}

export interface MentionCandidateOptions {
  /**
   * The active thread's participants. Present ⇒ every human candidate is
   * stamped `inThread`, and people already in the thread rank above people
   * who are not, within each match tier — the person you mean is far more
   * often someone already in the conversation.
   *
   * It also decides whether @team is offered at all: a room of one has nobody
   * to address as a group, and an unknown thread (absent) is not a room this
   * can speak for.
   */
  participantIds?: ReadonlySet<string> | null;
}

const DEFAULT_LIMIT = 8;
/** Chars the on-send `PLAIN_MENTION` regex accepts inside a `@query`. */
const HANDLE_SAFE = /[^A-Za-z0-9._%+-]/g;

function norm(v: string | undefined): string {
  return (v ?? '').trim().toLowerCase();
}
function nameKey(v: string | undefined): string {
  return norm(v).replace(/\s+/g, '');
}
function emailLocal(email: string | undefined): string {
  const e = norm(email);
  const at = e.indexOf('@');
  return at > 0 ? e.slice(0, at) : e;
}
/** Strip anything the resolver's plain-mention regex would not capture. */
function sanitizeHandle(v: string): string {
  return (v ?? '').trim().replace(HANDLE_SAFE, '');
}

/**
 * Derive the plain handle to insert for `member`, verified against `roster` so
 * `resolveMentionsToUids('@' + handle, roster)` returns exactly `[member.userId]`.
 * Tries the most readable forms first (email local-part, whitespace-stripped
 * display name), then falls back to the uid — which the resolver always matches
 * exactly and uniquely. Never returns empty for a member with a uid.
 */
export function deriveMentionHandle(
  member: MentionRosterEntry,
  roster: ReadonlyArray<MentionRosterEntry>,
): string {
  const uid = String(member?.userId ?? '').trim();
  if (!uid) return '';

  // READABLE FORMS ONLY (2026-09-08): the uid used to sit in this list, and
  // because a uid always verifies uniquely it won this loop the moment neither
  // readable form did — which is how a raw id reached a client-visible thread.
  // The uid is now reachable only through the floor below, where a member has
  // no other identifier in existence.
  // The profile's e-mail stands in for a blank member-doc e-mail (2026-09-08).
  const options = [emailLocal(member.email || member.profileEmail), nameKey(member.displayName)]
    .map(sanitizeHandle)
    .filter((h) => h.length > 0);

  for (const handle of options) {
    // A GROUP TOKEN IS NEVER A PERSON'S HANDLE (2026-09-18). Somebody named
    // "Team" would otherwise be offered `@team`, and the message would address
    // the whole room instead of them — the collision the short token list in
    // mentionResolver.TEAM_TOKENS exists to avoid, arriving from the other
    // direction. The verification below already fails for them (the server
    // expands the token to everyone, so it resolves to more than one uid), but
    // the unverified floor further down would hand it back anyway.
    if (isTeamMentionQuery(handle)) continue;
    const resolved = resolveMentionsToUids(`@${handle}`, roster);
    if (resolved.length === 1 && resolved[0] === uid) return handle;
  }

  // A RAW UID NEVER GOES INTO MESSAGE TEXT (2026-09-08 report): the old floor
  // returned the uid whenever no readable form verified, and a group thread in a
  // client's workspace showed `@kFZ5MgOgqkZn7tXy0XWtiW13hu72` mid-sentence. The
  // resolver's exact-identifier tier makes an email local-part unique per account,
  // so this floor is now essentially unreachable — but when it IS reached, a
  // readable handle that fails to summon beats an unreadable one that succeeds:
  // the sender can see and fix what they wrote, and the fail-closed rule means
  // nobody is summoned by mistake either way.
  const readable = [
    sanitizeHandle(emailLocal(member.email || member.profileEmail)),
    sanitizeHandle(nameKey(member.displayName)),
  ].find((h) => h.length > 0 && !isTeamMentionQuery(h));
  if (readable) return readable;
  // Nothing readable exists on this member at all (no email, no name): the uid
  // is the only identifier left, and the resolver matches it exactly.
  return sanitizeHandle(uid) || uid;
}

/**
 * The assistant's synthetic candidate (owner directive 2026-08-26: "when
 * someone mentions @p - top result should be @Prest0n"). Prest0n is not a
 * roster member, so without this the one participant every thread shares was
 * the one name the dropdown could not offer. The handle inserts the exact
 * spelling the summon gate's `mentionsAssistant` recognises, capital P per
 * the brand rule.
 */
export const ASSISTANT_MENTION_CANDIDATE: MentionCandidate = {
  uid: 'assistant',
  label: 'Prest0n',
  secondary: 'AI teammate',
  handle: 'Prest0n',
};

/**
 * The whole-room candidate (Miles, 2026-09-18: "there needs to be in each
 * thread an @team … it should be easy to @team @mention team to everyone").
 *
 * Synthetic, like Prest0n's above, and for the same reason: it is not a roster
 * member, so without a row here the one handle that addresses everybody would
 * be the one the dropdown could not offer — and a feature nobody can discover
 * is a feature nobody uses.
 *
 * The uid is the sentinel `team`, which no code path treats as a person:
 * `decideMentionSelection` inserts it outright (never the add-people ask — the
 * room is already in the room), and the on-send resolver on this side skips the
 * token entirely, so it can never reach a membership write.
 */
export const TEAM_MENTION_CANDIDATE: MentionCandidate = {
  uid: 'team',
  // The label is the bare word, like the assistant row's 'Prest0n' beside it:
  // the dropdown renders labels raw and the `@` is already typed.
  label: 'team',
  secondary: 'everyone in this thread',
  handle: TEAM_MENTION_HANDLE,
};

/** Query keys the team candidate answers to — the TEAM_TOKENS spellings. */
const TEAM_QUERY_KEYS = ['team', 'everyone'];

export function teamMatchesMentionQuery(query: string): boolean {
  const q = norm(query);
  if (!q) return true; // a bare "@" offers everyone — the room included
  return TEAM_QUERY_KEYS.some((k) => k.startsWith(q));
}

/** Query keys the assistant candidate answers to. */
const ASSISTANT_QUERY_KEYS = ['prest0n', 'preston'];

export function assistantMatchesMentionQuery(query: string): boolean {
  const q = norm(query);
  if (!q) return true; // a bare "@" offers everyone — Prest0n included
  return ASSISTANT_QUERY_KEYS.some((k) => k.startsWith(q) || k.includes(q));
}

/**
 * Rank the roster for the active `@query`. Excludes self and blank uids, de-dupes by
 * uid (first wins). Empty query → Prest0n, then the whole roster (label-sorted).
 * Otherwise, ranked by WHAT matched (owner directive 2026-08-26 — people are
 * found by their NAME; usernames/emails are the secondary form):
 *   0. Prest0n, whenever the query could mean it ("@p" → @Prest0n on top);
 *   1. display-name prefix matches;
 *   2. email / uid prefix matches;
 *   3. name substring matches;
 *   4. email / uid substring matches.
 * Ties sort by label. Returns at most `limit` candidates, each with a derived handle.
 */
export function filterMentionCandidates(
  roster: ReadonlyArray<MentionRosterEntry>,
  query: string,
  selfId: string | null | undefined,
  limit: number = DEFAULT_LIMIT,
  opts: MentionCandidateOptions = {},
): MentionCandidate[] {
  const self = String(selfId ?? '').trim();
  const q = norm(query);
  const participants = opts.participantIds ?? null;

  const seen = new Set<string>();
  const base: MentionRosterEntry[] = [];
  for (const m of roster ?? []) {
    const uid = String(m?.userId ?? '').trim();
    if (!uid || uid === self || seen.has(uid)) continue;
    seen.add(uid);
    base.push(m);
  }

  const labelOf = (m: MentionRosterEntry): string =>
    (m.displayName || '').trim() || (m.email || '').trim() || String(m.userId).trim();
  const byLabel = (a: MentionRosterEntry, b: MentionRosterEntry): number =>
    labelOf(a).toLocaleLowerCase().localeCompare(labelOf(b).toLocaleLowerCase()) ||
    String(a.userId).localeCompare(String(b.userId));

  let ranked: MentionRosterEntry[];
  if (!q) {
    ranked = [...base].sort(byLabel);
  } else {
    const scored = base
      .map((m) => {
        const nameKeys = [nameKey(m.displayName)].filter((k) => k.length > 0);
        const handleKeys = [norm(m.email), emailLocal(m.email), norm(m.userId)].filter(
          (k) => k.length > 0,
        );
        // Names beat usernames at every tier: a name PREFIX outranks a
        // username prefix, and a name SUBSTRING outranks a username
        // substring — the person someone is looking for is found by how
        // they're known, not by their login.
        const rank = nameKeys.some((k) => k.startsWith(q)) ? 0
          : handleKeys.some((k) => k.startsWith(q)) ? 1
          : nameKeys.some((k) => k.includes(q)) ? 2
          : handleKeys.some((k) => k.includes(q)) ? 3
          : 4;
        return { m, rank };
      })
      .filter((s) => s.rank < 4);
    scored.sort((a, b) => a.rank - b.rank || byLabel(a.m, b.m));
    ranked = scored.map((s) => s.m);
  }

  // People already in the thread first (2026-09-15), the rank order kept
  // within each half. Only when the caller knows who is in the thread.
  if (participants) {
    const inThread = ranked.filter((m) => participants.has(String(m.userId).trim()));
    const outside = ranked.filter((m) => !participants.has(String(m.userId).trim()));
    ranked = [...inThread, ...outside];
  }

  const humans = ranked.slice(0, Math.max(0, limit)).map((m) => {
    const label = labelOf(m);
    const email = (m.email || '').trim();
    const displayName = (m.displayName || '').trim();
    const uid = String(m.userId).trim();
    return {
      uid,
      label,
      secondary: displayName && email ? email : undefined,
      handle: deriveMentionHandle(m, roster),
      ...(participants ? { inThread: participants.has(uid) } : {}),
    };
  });

  // Prest0n rides on top whenever the query could mean it — never displacing
  // a human below the limit (the assistant is additive, not competitive).
  // @team rides with him, under him: naming the room is the broader act, and
  // the owner directive that put Prest0n first ("when someone mentions @p —
  // top result should be @Prest0n", 2026-08-26) is untouched by it.
  //
  // Offered only for a thread with somebody else in it. In a room of one there
  // is nobody to address, and for an unknown thread (no participant list) this
  // cannot tell — so it says nothing rather than offering a handle that might
  // address one person as "the team".
  const offerTeam = !!participants && participants.size > 1 && teamMatchesMentionQuery(q);
  return [
    ...(assistantMatchesMentionQuery(q) ? [ASSISTANT_MENTION_CANDIDATE] : []),
    ...(offerTeam ? [TEAM_MENTION_CANDIDATE] : []),
    ...humans,
  ];
}
